import React from 'react';
import PropTypes from 'prop-types';
import saveAs from 'file-saver';
import { connect } from 'react-redux';
import { faSpinner } from '@fortawesome/pro-regular-svg-icons/faSpinner';

import FontAwesome from 'utils/FA';
import registeredComponent from 'utils/registeredComponent';
import { modals } from 'ducks';
import { getMetaContent } from 'utils';
import ModalComponent from './ModalComponent';

const retryStatuses = ['pending', 'started'];
const successStatuses = ['completed', 'newly_completed'];
const failedStatuses = ['failed'];

class Downloader extends React.Component {
  // Simple React component that polls a url to see if a file is returned and presents it for download

  static propTypes = {
    dispatch: PropTypes.func,
    url: PropTypes.string,
    params: PropTypes.object,
    async: PropTypes.bool,
    message: PropTypes.string,
    // indicates whether the modal should close as soon as the file is downloaded
    autoClose: PropTypes.bool,
    // GET, POST, PUT use by fetchItem
    httpMethod: PropTypes.string,
    pollInterval: PropTypes.number
  };

  static defaultProps = {
    dispatch: () => false,
    async: true,
    url: '',
    autoClose: false,
    httpMethod: 'POST',
    message: 'Your file is being prepared.',
    pollInterval: 3000
  };

  state = {
    status: 'pending'
  };

  componentDidMount() {
    // checks whether we're going to poll a url or just download a file
    this.props.async ? this.pollForItem() : this.fetchItem();
  }

  pollForItem() {
    const { dispatch, httpMethod, url, params, pollInterval } = this.props;
    const token = getMetaContent('csrf-token');

    fetch(url, {
      credentials: 'same-origin',
      headers: {'Content-Type': 'application/json', 'X-CSRF-Token': token},
      method: httpMethod,
      body: JSON.stringify(params)
    })
      .then(response => response.json())
      .then(jobInfo => {
        if (jobInfo.error) {
          throw new Error(jobInfo.error)
        } else {
          return pollUrl(jobInfo, pollInterval).catch(() =>  dispatch(modals.actions.show('UnexpectedError'))
          )
        }
      })
      .then(response => {
        this.processFile(response, dispatch);
      })
      .catch((error) => {
        // Specifically for errors from the initial controller before we start polling
        dispatch(modals.actions.hide());
        if (error && error.message) {
          dispatch(modals.actions.show('UnexpectedError', {message: error.message}));
        } else {
          dispatch(modals.actions.show('UnexpectedError'));
        }
      });
  }

  fetchItem() {
    const {dispatch, url, params, httpMethod} = this.props;
    let token = getMetaContent('csrf-token');

    fetch(url, {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
      method: httpMethod,
      body: JSON.stringify(params)
    })
      .then(response => this.processFile(response, dispatch))
      .catch(() => {
        dispatch(modals.actions.hide());
        dispatch(modals.actions.show('UnexpectedError'));
      });
  }

  // processes and downloads a file
  processFile(response, dispatch) {
    const { autoClose } = this.props;

    response.blob()
      .then(blob => {
        if (response.status === 200) {
          const contentDisposition = response.headers.get('content-disposition') || '';
          const match = contentDisposition.match(/filename="(.*)"/)
            || contentDisposition.match(/filename='(.*)'/);
          const filename = match && match[1];

          saveAs(blob, filename || 'downloaded_file.csv');

          if (autoClose) {
            dispatch(modals.actions.hide());
          } else {
            this.setState({ status: 'done' });
          }
        } else {
          dispatch(modals.actions.hide());
          dispatch(modals.actions.show('UnexpectedError'));
        }
      })
      .catch(() => {
        dispatch(modals.actions.hide());
        dispatch(modals.actions.show('UnexpectedError'));
      });
  }

  render() {
    const { status } = this.state;
    const { message } = this.props;

    return (
      <ModalComponent title='Please Wait'>
        {status === 'pending'
          ? (
            <div>
              {message}
              <FontAwesome icon={faSpinner} spin preNbsp />
            </div>
          )
          : <div>Your file download has completed. You may now close the modal.</div>}
      </ModalComponent>
    );
  }
}


// polls a job url until the job finishes
const pollUrl = (jobInfo, pollInterval) => (
  new Promise((resolve, reject) => {

    let timeOut = null;
    const token = getMetaContent('csrf-token');

    const recurse = () => {
      fetch(`/api/user_jobs/user_jobs/${jobInfo.user_job_id}`, {
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
        method: 'GET'
      })
        .then(response => response.json())
        .then(responseJson => {
          // the job is done and we can get a file
          const jobType = jobInfo.job_type;
          const status = responseJson[jobType] && responseJson[jobType].status;

          if (successStatuses.includes(status)) {
            // get the file
            fetch(`/api/user_jobs/user_jobs/${jobInfo.user_job_id}/attachment`, {
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
              method: 'GET'
            })
              .then(response => {
                if (timeOut) {
                  clearTimeout(timeOut);
                }
                resolve(response);
              })
              .catch(response => reject(response));
          } else if (retryStatuses.includes(status)) {
            timeOut = setTimeout(() => {
              recurse();
            }, pollInterval);
          } else if (failedStatuses.includes(status)) {
            reject(new Error(status));
          } else {
            reject(new Error(`Unknown status: ${status}`));
          }
        });
    };
    recurse();
  })
);

export default registeredComponent('Downloader')(connect()(Downloader));
